/**
 * Dashboard report schemas — "work command center" (2026-09-27 rebuild).
 *
 * The payload is role-shaped: Staff (no dashboard.firmwide) get the "my-day"
 * view — My Work, a scope-filtered deadline ladder, and their own hours only.
 * All other staff roles get the "full" view with the five command-center
 * blocks; the proposals sub-block stays hidden for TeamLead (dashboard.funnel).
 * Money is deliberately off the dashboard: no invoices, fees, or goal revenue.
 */

import { z } from 'zod';
import { JURISDICTION_CODES } from '../reference/jurisdictions';
import { itemStatusSchema } from './compliance-item';
import { dashboardLayoutSchema } from './dashboard-layout';
import { receivableClientBalanceSchema, receivableCurrencySummarySchema } from './invoice';

const jurisdictionCodeSchema = z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]);

/** Compliance-item row shared by My Work / ladder / blocked lists. */
export const dashboardItemRowSchema = z.object({
  id: z.string().uuid(),
  serviceType: z.string(),
  clientName: z.string(),
  jurisdiction: jurisdictionCodeSchema.nullable(),
  deadline: z.string().datetime().nullable(),
  dueDate: z.string().datetime().nullable().default(null),
  status: itemStatusSchema,
  assignee1Name: z.string().nullable(),
  assignee2Name: z.string().nullable(),
  managerName: z.string().nullable(),
});

export const dashboardBlockedReasonSchema = z.enum(['WaitingOnClient', 'SentBack', 'Stale']);

export const dashboardBlockedRowSchema = dashboardItemRowSchema.extend({
  reason: dashboardBlockedReasonSchema,
  /** assignee1 ?? manager. */
  ownerName: z.string().nullable(),
  daysStuck: z.number().int(),
});

export const dashboardEngagementRowSchema = z.object({
  id: z.string().uuid(),
  title: z.string().nullable(),
  clientName: z.string(),
  jurisdiction: jurisdictionCodeSchema.nullable(),
  pipeline: z.string(),
  startDate: z.string().datetime().nullable(),
  endDate: z.string().datetime().nullable(),
});

export const dashboardEngagementTaskRowSchema = z.object({
  id: z.string().uuid(),
  engagementId: z.string().uuid(),
  engagementTitle: z.string().nullable(),
  clientName: z.string(),
  title: z.string(),
  endDate: z.string().datetime().nullable(),
  status: z.enum(['PENDING', 'IN_PROCESS', 'COMPLETED']),
});

/** ① My Work — own assigned open compliance items, engagements and checklist tasks. */
export const dashboardMyWorkSchema = z.object({
  /** Combined open compliance + engagement count (checklist tasks are separate). */
  count: z.number().int(),
  complianceCount: z.number().int(),
  engagementCount: z.number().int(),
  engagementTaskCount: z.number().int(),
  items: z.array(dashboardItemRowSchema),
  engagements: z.array(dashboardEngagementRowSchema),
  engagementTasks: z.array(dashboardEngagementTaskRowSchema),
});

/** Today's meetings — meetings the caller organizes or attends, starting today. */
export const dashboardMeetingRowSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startAt: z.string().datetime(),
  durationMin: z.number().int(),
  location: z.string().nullable(),
  meetingUrl: z.string().nullable(),
  clientName: z.string().nullable(),
});

/** ② One rung of the deadline ladder. `dueAfter`/`dueBefore` are the exact
 *  window bounds — the web links straight to
 *  `/compliance?open=true&dueDateAfter=…&dueDateBefore=…` with them. */
export const dashboardDeadlineWindowSchema = z.object({
  count: z.number().int(),
  /** Top rows by actual due date ascending. */
  items: z.array(dashboardItemRowSchema),
  /** Null when the window has no lower bound (overdue). */
  dueAfter: z.string().datetime().nullable(),
  dueBefore: z.string().datetime(),
});

export const dashboardDeadlineLadderSchema = z.object({
  /** Before today. */
  overdue: dashboardDeadlineWindowSchema,
  /** Today only. */
  today: dashboardDeadlineWindowSchema,
  /** Tomorrow → end of week (Sunday). */
  week: dashboardDeadlineWindowSchema,
  /** Day after week end → end of month. */
  month: dashboardDeadlineWindowSchema,
  /** Open items with no due date. */
  unscheduled: z.object({ count: z.number().int(), items: z.array(dashboardItemRowSchema) }),
});

/** ③ Workload by person (Manager/TeamLead/Staff only — CEO/GM excluded). */
export const dashboardWorkloadRowSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  role: z.string(),
  openItems: z.number().int(),
  overdueItems: z.number().int(),
});

export const dashboardWorkloadSchema = z.object({
  managers: z.array(dashboardWorkloadRowSchema),
  teamLeads: z.array(dashboardWorkloadRowSchema),
  staff: z.array(dashboardWorkloadRowSchema),
});

export const dashboardTeamExceptionRowSchema = dashboardWorkloadRowSchema.extend({
  blockedItems: z.number().int(),
  noTimeToday: z.boolean(),
  missingCheckout: z.boolean(),
});

export const dashboardProposalRowSchema = z.object({
  id: z.string().uuid(),
  subject: z.string(),
  clientName: z.string(),
  status: z.string(),
});

/** ④ Pipeline strip. `proposals` is null for roles without proposal
 *  visibility (dashboard.funnel — TeamLead). */
export const dashboardPipelineSchema = z.object({
  engagements: z.object({
    /** Engagements in a live stage (not DEFERRED / NOT_INTERESTED). */
    inProgress: z.number().int(),
    byStage: z.array(z.object({ stage: z.string(), count: z.number().int() })),
  }),
  proposals: z
    .object({
      draft: z.number().int(),
      /** SENT + VIEWED. */
      sent: z.number().int(),
      top: z.array(dashboardProposalRowSchema),
    })
    .nullable(),
  blocked: z.object({
    count: z.number().int(),
    top: z.array(dashboardBlockedRowSchema),
  }),
});

/** ⑤ Team hours — per-person today + this week, plus team totals. */
export const dashboardTeamHoursSchema = z.object({
  rows: z.array(
    z.object({
      userId: z.string().uuid(),
      displayName: z.string(),
      role: z.string(),
      today: z.number(),
      week: z.number(),
    }),
  ),
  totalToday: z.number(),
  totalWeek: z.number(),
});

export const dashboardInvoiceStatusSchema = z.object({
  draft: z.number().int(),
  issued: z.number().int(),
  overdue: z.number().int(),
  partiallyPaid: z.number().int(),
  paid: z.number().int(),
  recent: z.array(z.object({
    id: z.string().uuid(), invoiceNumber: z.string(), clientName: z.string(), status: z.string(),
    total: z.number().int(), currency: z.string(), dueDate: z.string().datetime(),
  })),
});

export const dashboardGoalProgressSchema = z.object({
  id: z.string().uuid(), title: z.string(), scopeType: z.string(), metric: z.string(), period: z.string(),
  currentValue: z.number(), targetValue: z.number(),
});

export const dashboardAttendanceSchema = z.object({
  missingCheckout: z.array(dashboardTeamExceptionRowSchema),
  pendingLeave: z.array(z.object({
    id: z.string().uuid(), userName: z.string(), type: z.string(),
    fromDate: z.string().datetime(), toDate: z.string().datetime(),
  })),
});

export const dashboardFullOutputSchema = z.object({
  view: z.literal('full'),
  layout: dashboardLayoutSchema,
  myWork: dashboardMyWorkSchema,
  todaysMeetings: z.array(dashboardMeetingRowSchema),
  deadlineLadder: dashboardDeadlineLadderSchema,
  workload: dashboardWorkloadSchema,
  teamExceptions: z.object({ rows: z.array(dashboardTeamExceptionRowSchema) }),
  pipeline: dashboardPipelineSchema,
  teamHours: dashboardTeamHoursSchema,
  invoiceStatus: dashboardInvoiceStatusSchema.nullable(),
  receivables: z.object({ summary: z.array(receivableCurrencySummarySchema), clients: z.array(receivableClientBalanceSchema) }).nullable(),
  goalProgress: z.array(dashboardGoalProgressSchema),
  attendance: dashboardAttendanceSchema,
});

export const dashboardMyDayOutputSchema = z.object({
  view: z.literal('my-day'),
  layout: dashboardLayoutSchema,
  myWork: dashboardMyWorkSchema,
  todaysMeetings: z.array(dashboardMeetingRowSchema),
  /** Scoped to the caller's own items via the shared item scope. */
  deadlineLadder: dashboardDeadlineLadderSchema,
  /** Own hours only — no team-wide figures for Staff. */
  myHours: z.object({ today: z.number(), week: z.number() }),
});

export const dashboardOutputSchema = z.discriminatedUnion('view', [
  dashboardFullOutputSchema,
  dashboardMyDayOutputSchema,
]);

export type DashboardItemRow = z.infer<typeof dashboardItemRowSchema>;
export type DashboardEngagementRow = z.infer<typeof dashboardEngagementRowSchema>;
export type DashboardEngagementTaskRow = z.infer<typeof dashboardEngagementTaskRowSchema>;
export type DashboardMeetingRow = z.infer<typeof dashboardMeetingRowSchema>;
export type DashboardBlockedRow = z.infer<typeof dashboardBlockedRowSchema>;
export type DashboardDeadlineWindow = z.infer<typeof dashboardDeadlineWindowSchema>;
export type DashboardWorkloadRow = z.infer<typeof dashboardWorkloadRowSchema>;
export type DashboardPipeline = z.infer<typeof dashboardPipelineSchema>;
export type DashboardFullOutput = z.infer<typeof dashboardFullOutputSchema>;
export type DashboardMyDayOutput = z.infer<typeof dashboardMyDayOutputSchema>;
export type DashboardOutput = z.infer<typeof dashboardOutputSchema>;
